import { useEffect } from 'react';
import { create } from 'zustand';

import type { LabelKind } from '@erikdakoda/label/getLabelKind';

/** Kind of sidebar mutation still waiting on the server after a drop or menu action. */
export type LabelPendingMutation = 'move' | 'copy' | 'remove' | 'reorder' | null;

const DEFAULT_POOF_WIDTH = 200;
const DEFAULT_POOF_HEIGHT = 40;

type LabelDragState = {
  /** True while an item drag that started in a label sidebar is in flight. */
  isDragging: boolean;
  /** Alt/Option held during the current drag — the copy modifier. */
  copyModifier: boolean;
  /** Kind of the section the drag started in. */
  sourceKind: LabelKind | null;
  /** Kind of the section currently hovered, or `null` when over nothing droppable. */
  destinationKind: LabelKind | null;
  /** True while the drag preview is over the remove zone. */
  overRemoveZone: boolean;
  /** Latest pointer position during a drag (viewport coords). */
  pointerX: number;
  pointerY: number;
  /** Dragged list-item width in px (from the drag preview). */
  dragItemWidth: number;
  /** Dragged list-item height in px (from the drag preview). */
  dragItemHeight: number;
  /** Incremented on every removal so `Poof` plays once per remove. */
  poofBurst: number;
  /** Viewport X where the last remove poof should appear. */
  poofX: number;
  /** Viewport Y where the last remove poof should appear. */
  poofY: number;
  /** Item width captured at remove for the poof ellipse. */
  poofWidth: number;
  /** Item height captured at remove for the poof ellipse. */
  poofHeight: number;
  /** Non-null while a drop/menu mutation has optimistic UI but has not finished on the server. */
  pendingMutation: LabelPendingMutation;
};

type LabelDragAction = {
  startDrag: (sourceKind: LabelKind) => void;
  endDrag: () => void;
  setCopyModifier: (copyModifier: boolean) => void;
  setDestinationKind: (destinationKind: LabelKind | null) => void;
  setOverRemoveZone: (overRemoveZone: boolean) => void;
  setPointer: (x: number, y: number) => void;
  setDragItemSize: (width: number, height: number) => void;
  triggerPoof: () => void;
  setPendingMutation: (pendingMutation: LabelPendingMutation) => void;
};

/**
 * Cross-component state for an in-flight label-sidebar drag. `copyModifier` is intentionally not
 * reset by `endDrag`: the drop handler reads it after the drag preview has already unmounted.
 */
export const useLabelDragStore = create<LabelDragState & LabelDragAction>()((set) => ({
  isDragging: false,
  copyModifier: false,
  sourceKind: null,
  destinationKind: null,
  overRemoveZone: false,
  pointerX: 0,
  pointerY: 0,
  dragItemWidth: 0,
  dragItemHeight: 0,
  poofBurst: 0,
  poofX: 0,
  poofY: 0,
  poofWidth: DEFAULT_POOF_WIDTH,
  poofHeight: DEFAULT_POOF_HEIGHT,
  pendingMutation: null,

  startDrag: (sourceKind) =>
    set(() => ({
      isDragging: true,
      sourceKind,
      copyModifier: false,
      overRemoveZone: false,
      dragItemWidth: 0,
      dragItemHeight: 0,
    })),
  endDrag: () =>
    set(() => ({
      isDragging: false,
      sourceKind: null,
      destinationKind: null,
      overRemoveZone: false,
    })),
  setCopyModifier: (copyModifier) =>
    set((state) => (state.copyModifier === copyModifier ? state : { copyModifier })),
  setDestinationKind: (destinationKind) =>
    set((state) => (state.destinationKind === destinationKind ? state : { destinationKind })),
  setOverRemoveZone: (overRemoveZone) =>
    set((state) => (state.overRemoveZone === overRemoveZone ? state : { overRemoveZone })),
  setPointer: (pointerX, pointerY) => set(() => ({ pointerX, pointerY })),
  setDragItemSize: (dragItemWidth, dragItemHeight) =>
    set(() => ({ dragItemWidth, dragItemHeight })),
  triggerPoof: () =>
    set((state) => ({
      poofBurst: state.poofBurst + 1,
      poofX: state.pointerX,
      poofY: state.pointerY,
      poofWidth: state.dragItemWidth > 0 ? state.dragItemWidth : DEFAULT_POOF_WIDTH,
      poofHeight: state.dragItemHeight > 0 ? state.dragItemHeight : DEFAULT_POOF_HEIGHT,
    })),
  setPendingMutation: (pendingMutation) =>
    set((state) => (state.pendingMutation === pendingMutation ? state : { pendingMutation })),
}));

/**
 * Mirrors the Alt/Option key into the drag store while a drag is active. `onDragEnd` carries no
 * modifier state, so the key has to be tracked separately for the duration of the drag.
 */
export function useLabelCopyModifier(): void {
  const isDragging = useLabelDragStore((state) => state.isDragging);
  const setCopyModifier = useLabelDragStore((state) => state.setCopyModifier);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }
    const syncFromKeyboard = (event: KeyboardEvent): void => setCopyModifier(event.altKey);
    const reset = (): void => setCopyModifier(false);
    window.addEventListener('keydown', syncFromKeyboard);
    window.addEventListener('keyup', syncFromKeyboard);
    window.addEventListener('blur', reset);
    return () => {
      window.removeEventListener('keydown', syncFromKeyboard);
      window.removeEventListener('keyup', syncFromKeyboard);
      window.removeEventListener('blur', reset);
    };
  }, [isDragging, setCopyModifier]);
}

/**
 * Tracks the pointer while a label-sidebar item drag is active so a remove poof can appear where
 * the user released.
 */
export function useLabelDragPointer(): void {
  const isDragging = useLabelDragStore((state) => state.isDragging);
  const setPointer = useLabelDragStore((state) => state.setPointer);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }
    const onMove = (event: PointerEvent): void => setPointer(event.clientX, event.clientY);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onMove);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onMove);
    };
  }, [isDragging, setPointer]);
}
